<template>
  <div class="departments-list">
    <!-- Header -->
    <b-card no-body>
      <b-card-header>
        <b-card-title>{{ $t('departments.title') }}</b-card-title>
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          size="sm"
          @click="showCreateModal"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          <span class="align-middle">{{ $t('departments.actions.create') }}</span>
        </b-button>
      </b-card-header>

      <!-- Filters -->
      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('departments.filters.search')"
              label-for="search"
            >
              <b-input-group>
                <b-form-input
                  id="search"
                  v-model="filters.search"
                  :placeholder="$t('departments.placeholders.search')"
                  @keyup.enter="handleSearch"
                />
                <b-input-group-append>
                  <b-button
                    variant="primary"
                    @click="handleSearch"
                  >
                    <feather-icon icon="SearchIcon" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="2"
          >
            <b-form-group
              :label="$t('departments.filters.status')"
              label-for="status-filter"
            >
              <v-select
                id="status-filter"
                v-model="filters.isActive"
                :options="statusOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('departments.status.all')"
                clearable
                @input="handleSearch"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="2"
          >
            <b-form-group
              :label="$t('departments.fields.manager')"
              label-for="manager-filter"
            >
              <v-select
                id="manager-filter"
                v-model="filters.hasManager"
                :options="managerOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('departments.status.all')"
                clearable
                @input="handleSearch"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="2"
          >
            <b-form-group
              :label="$t('departments.fields.parent')"
              label-for="parent-filter"
            >
              <v-select
                id="parent-filter"
                v-model="filters.parentId"
                :options="parentOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('departments.status.all')"
                clearable
                @input="handleSearch"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
            class="d-flex align-items-end"
          >
            <b-button
              variant="outline-secondary"
              size="sm"
              class="mr-1"
              @click="resetFilters"
            >
              <feather-icon
                icon="RefreshCwIcon"
                class="mr-50"
              />
              {{ $t('common.reset') }}
            </b-button>
            <!-- <b-button
              variant="outline-primary"
              size="sm"
              @click="$router.push('/departments/tree')"
            >
              <feather-icon icon="GitBranchIcon" class="mr-50" />
              {{ $t('departments.tree') }}
            </b-button> -->
          </b-col>
        </b-row>
      </b-card-body>

      <!-- Table -->
      <b-table
        ref="departmentsTable"
        :items="departments"
        :fields="tableColumns"
        :busy="loading"
        responsive
        striped
        hover
        show-empty
        :empty-text="$t('departments.messages.no_data')"
        class="departments-table"
        tbody-tr-class="department-row"
      >
        <!-- Loading State -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>{{ $t('common.loading') }}</strong>
          </div>
        </template>

        <!-- Code -->
        <template #cell(code)="data">
          <b-badge
            variant="light-secondary"
            class="text-monospace"
          >
            {{ data.item.code }}
          </b-badge>
        </template>

        <!-- Name with Path -->
        <template #cell(name)="data">
          <div>
            <strong>{{ data.item.name }}</strong>
            <br>
            <small class="text-muted">{{ data.item.full_path }}</small>
          </div>
        </template>

        <!-- Parent -->
        <template #cell(parent)="data">
          <span v-if="data.item.parent">
            {{ data.item.parent.name }}
          </span>
          <b-badge
            v-else
            variant="light-info"
          >
            {{ $t('departments.table.no_parent') }}
          </b-badge>
        </template>

        <!-- Manager -->
        <template #cell(manager)="data">
          <div v-if="data.item.manager">
            <span>{{ data.item.manager.full_name }}</span>
            <br>
            <small class="text-muted">{{ data.item.manager.employee_code }}</small>
          </div>
          <b-badge
            v-else
            variant="light-warning"
          >
            {{ $t('departments.table.no_manager') }}
          </b-badge>
        </template>

        <!-- Employees Count -->
        <template #cell(employees_count)="data">
          <b-badge
            variant="light-success"
            class="mr-50"
          >
            {{ data.item.active_employees_count }}
          </b-badge>
          <span class="text-muted">/ {{ data.item.employees_count }}</span>
        </template>

        <!-- Children Count -->
        <template #cell(children_count)="data">
          <b-badge
            :variant="data.item.children_count > 0 ? 'light-primary' : 'light-secondary'"
          >
            {{ data.item.children_count }}
          </b-badge>
        </template>

        <!-- Status -->
        <template #cell(is_active)="data">
          <b-badge
            :variant="data.item.is_active ? 'light-success' : 'light-secondary'"
            class="cursor-pointer"
            @click="toggleStatus(data.item)"
          >
            {{ data.item.is_active ? $t('departments.status.active') : $t('departments.status.inactive') }}
          </b-badge>
        </template>

        <!-- Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            toggle-class="table-dropdown-toggle"
            menu-class="table-dropdown-menu"
            :right="$store.state.appConfig.isRTL"
            boundary="viewport"
            offset="8"
            no-flip
            dropleft
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              class="dropdown-item-custom"
              @click="viewDepartment(data.item.id)"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
                class="mr-50"
              />
              <span class="align-middle">{{ $t('departments.actions.view') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              class="dropdown-item-custom"
              @click="editDepartment(data.item)"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-50"
              />
              <span class="align-middle">{{ $t('departments.actions.edit') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              class="dropdown-item-custom"
              @click="manageManager(data.item)"
            >
              <feather-icon
                icon="UserIcon"
                size="14"
                class="mr-50"
              />
              <span class="align-middle">{{ $t('departments.modal.manage_manager') }}</span>
            </b-dropdown-item>

            <b-dropdown-divider />

            <b-dropdown-item
              class="dropdown-item-custom dropdown-item-danger"
              @click="confirmDelete(data.item)"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
                class="mr-50"
              />
              <span class="align-middle">{{ $t('departments.actions.delete') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-card-footer v-if="pagination.total > 0">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              {{ $t('departments.table.showing') }} {{ (pagination.currentPage - 1) * pagination.perPage + 1 }}
              {{ $t('departments.table.to') }} {{ Math.min(pagination.currentPage * pagination.perPage, pagination.total) }}
              {{ $t('departments.table.of') }} {{ pagination.total }} {{ $t('departments.table.entries') }}
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
          >
            <b-pagination
              v-model="pagination.currentPage"
              :total-rows="pagination.total"
              :per-page="pagination.perPage"
              align="right"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              @change="handlePageChange"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </b-card-footer>
    </b-card>

    <!-- Create/Edit Modal -->
    <department-form-modal
      v-model="showFormModal"
      :department="selectedDepartment"
      :mode="formMode"
      @submitted="handleFormSubmitted"
    />

    <!-- Manager Assignment Modal -->
    <manager-assignment-modal
      v-model="showManagerModal"
      :department="selectedDepartment"
      @assigned="handleManagerAssigned"
    />
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BCardFooter,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BTable,
  BBadge,
  BPagination,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapState, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import DepartmentFormModal from './components/DepartmentFormModal.vue'
import ManagerAssignmentModal from './components/ManagerAssignmentModal.vue'

export default {
  name: 'DepartmentsList',

  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BCardFooter,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BTable,
    BBadge,
    BPagination,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BSpinner,
    vSelect,
    DepartmentFormModal,
    ManagerAssignmentModal,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      filters: {
        search: '',
        isActive: null,
        hasManager: null,
        parentId: null,
      },
      showFormModal: false,
      showManagerModal: false,
      selectedDepartment: null,
      formMode: 'create',
    }
  },

  computed: {
    ...mapState('departments', ['departments', 'pagination', 'loading']),

    tableColumns() {
      return [
        { key: 'code', label: this.$t('departments.table.code'), sortable: true },
        { key: 'name', label: this.$t('departments.table.department'), sortable: true },
        { key: 'parent', label: this.$t('departments.table.parent') },
        { key: 'manager', label: this.$t('departments.table.manager') },
        { key: 'employees_count', label: this.$t('departments.table.employees'), class: 'text-center' },
        { key: 'children_count', label: this.$t('departments.fields.children_count'), class: 'text-center' },
        { key: 'is_active', label: this.$t('departments.table.status'), class: 'text-center' },
        { key: 'actions', label: this.$t('departments.table.actions'), class: 'text-center' },
      ]
    },

    statusOptions() {
      return [
        { label: this.$t('departments.status.active'), value: true },
        { label: this.$t('departments.status.inactive'), value: false },
      ]
    },

    managerOptions() {
      return [
        { label: this.$t('departments.filters.has_manager'), value: true },
        { label: this.$t('departments.messages.no_manager'), value: false },
      ]
    },

    parentOptions() {
      return [
        { label: this.$t('departments.table.no_parent'), value: 'null' },
      ]
    },
  },

  created() {
    this.loadDepartments()
  },

  methods: {
    ...mapActions('departments', [
      'fetchDepartments',
      'deleteDepartment',
      'toggleStatus',
    ]),

    async loadDepartments() {
      try {
        await this.fetchDepartments({
          ...this.filters,
          page: this.pagination.currentPage,
          perPage: this.pagination.perPage,
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.message || this.$t('departments.messages.load_error'),
            variant: 'danger',
          },
        })
      }
    },

    handleSearch() {
      this.pagination.currentPage = 1
      this.loadDepartments()
    },

    handlePageChange(page) {
      this.pagination.currentPage = page
      this.loadDepartments()
    },

    resetFilters() {
      this.filters = {
        search: '',
        isActive: null,
        hasManager: null,
        parentId: null,
      }
      this.pagination.currentPage = 1
      this.loadDepartments()
    },

    showCreateModal() {
      this.formMode = 'create'
      this.selectedDepartment = null
      this.showFormModal = true
    },

    viewDepartment(id) {
      this.$router.push({ name: 'departments-view', params: { id } })
    },

    editDepartment(department) {
      this.formMode = 'edit'
      this.selectedDepartment = department
      this.showFormModal = true
    },

    manageManager(department) {
      this.selectedDepartment = department
      this.showManagerModal = true
    },

    async toggleStatus(department) {
      try {
        await this.toggleStatus(department.id)
        const statusText = department.is_active ? this.$t('departments.messages.deactivate_success') : this.$t('departments.messages.activate_success')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.success'),
            icon: 'CheckIcon',
            text: statusText,
            variant: 'success',
          },
        })
        this.loadDepartments()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.message || this.$t('common.error'),
            variant: 'danger',
          },
        })
      }
    },

    confirmDelete(department) {
      this.$swal({
        title: this.$t('departments.messages.confirm_delete'),
        text: this.$t('departments.messages.confirm_delete_text'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.yes'),
        cancelButtonText: this.$t('common.cancel'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.handleDelete(department.id)
        }
      })
    },

    async handleDelete(id) {
      try {
        await this.deleteDepartment(id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.success'),
            icon: 'CheckIcon',
            text: this.$t('departments.messages.delete_success'),
            variant: 'success',
          },
        })
        this.loadDepartments()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.message || this.$t('departments.messages.delete_error'),
            variant: 'danger',
          },
        })
      }
    },

    handleFormSubmitted() {
      this.showFormModal = false
      this.selectedDepartment = null
      this.loadDepartments()
    },

    handleManagerAssigned() {
      this.showManagerModal = false
      this.selectedDepartment = null
      this.loadDepartments()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.cursor-pointer {
  cursor: pointer;
}

// Table dropdown styling fixes
.table-dropdown-toggle {
  padding: 0.375rem 0.5rem !important;
  border: none !important;
  background: transparent !important;

  &:hover,
  &:focus {
    background: rgba(113, 102, 240, 0.1) !important;
    border-radius: 0.375rem;
  }

  &::after {
    display: none !important;
  }
}

// Custom dropdown menu styling
:deep(.table-dropdown-menu) {
  min-width: 160px;
  padding: 0.5rem 0;
  border: 1px solid #e3e6f0;
  border-radius: 0.5rem;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
  background: #fff;
  z-index: 1050;

  // Ensure proper positioning
  position: absolute;
  will-change: transform;
  transform: translate3d(0px, 0px, 0px);

  // Fix scrollbar overlap
  margin-right: 0;
  right: auto;

  .dropdown-item-custom {
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: #5a5c69;
    transition: all 0.15s ease-in-out;

    &:hover {
      background-color: #f8f9fc;
      color: #3a3b45;
      text-decoration: none;
    }

    &:focus {
      background-color: #f8f9fc;
      color: #3a3b45;
      outline: none;
    }

    .feather-icon {
      color: inherit;
      margin-right: 0.5rem;
      flex-shrink: 0;
    }
  }

  .dropdown-item-danger {
    color: #e74a3b;

    &:hover {
      background-color: #f8d7da;
      color: #721c24;
    }

    &:focus {
      background-color: #f8d7da;
      color: #721c24;
    }
  }

  .dropdown-divider {
    margin: 0.5rem 0;
    border-top: 1px solid #e3e6f0;
  }
}

// Table specific fixes
:deep(.table) {
  .dropdown {
    position: relative;

    .dropdown-menu {
      position: absolute;
      top: 100%;
      left: auto;
      right: 0;
      margin-top: 0.125rem;

      // Prevent scrollbar overlap
      &::before {
        content: '';
        position: absolute;
        top: -0.5rem;
        left: 0;
        right: 0;
        height: 0.5rem;
        background: transparent;
      }
    }
  }
}

// Responsive dropdown positioning
@media (max-width: 768px) {
  :deep(.table-dropdown-menu) {
    right: 0;
    left: auto;
    min-width: 140px;
  }
}

// Fix for RTL layout
[dir="rtl"] {
  :deep(.table-dropdown-menu) {
    right: auto;
    left: 0;
  }
}

// Table row height fixes
.departments-table {
  :deep(.department-row) {
    min-height: 60px;
    vertical-align: middle;

    td {
      padding: 0.75rem 0.5rem;
      vertical-align: middle;
      border-top: 1px solid #dee2e6;
    }
  }

  // Ensure actions column has enough space
  :deep(.table td:nth-last-child(1)) {
    min-width: 120px;
    white-space: nowrap;
  }
}

// Dropdown container fixes
:deep(.table) {
  .dropdown {
    position: static;
    display: inline-block;

    .dropdown-menu {
      position: absolute;
      top: 100%;
      right: 0;
      left: auto;
      margin-top: 0.25rem;
      min-width: 160px;
      z-index: 1060;
      transform: none;

      // Ensure dropdown is visible
      &[style*="display: block"] {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
    }
  }
}

// Fix table overflow
.departments-table {
  :deep(.table-responsive) {
    overflow: visible !important;
    border: none;
  }

  :deep(.table) {
    margin-bottom: 0;
    overflow: visible;
    table-layout: auto;

    // Ensure proper cell spacing
    td, th {
      padding: 0.75rem 0.5rem;
      vertical-align: middle;
    }
  }
}

// Card body overflow fix
.b-card-body {
  overflow: visible !important;
}

// Ensure proper spacing for dropdown
:deep(.table-dropdown-toggle) {
  position: relative;
  z-index: 1;

  &:focus {
    z-index: 2;
  }
}
</style>
